import { useState } from 'react';
import { api } from '../lib/api.js';
import { todayStr } from '../lib/format.js';

// Modal for submitting a timeclock correction request. Used both on
// the punch banner ("forgot to clock in/out") and on Recent activity
// ("dispute / request correction" path). After submit the request
// goes into the manager queue at Schedule → Time Card.
export function CorrectionRequestModal({ user, defaults, onClose, onSubmitted }) {
  const headers = { 'x-user-id': String(user.id) };
  const [shiftDate, setShiftDate] = useState(defaults?.shift_date || todayStr());
  const [reqType, setReqType] = useState(defaults?.request_type || 'add_punch');
  const [punchId] = useState(defaults?.punch_id || null);
  const [inTime, setInTime]   = useState(defaults?.proposed_clock_in_local  || '09:00');
  const [outTime, setOutTime] = useState(defaults?.proposed_clock_out_local || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  function localToIso(date, hm) {
    if (!date || !hm) return null;
    // We send full ISO with the NY offset embedded. Server stores in
    // timestamptz so the offset is just a parse hint.
    const [h, m] = hm.split(':').map(Number);
    const d = new Date(`${date}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`);
    return d.toISOString();
  }

  async function submit() {
    if (!reason.trim()) { setErr('Please add a reason — your manager needs context.'); return; }
    setBusy(true); setErr('');
    try {
      await api('POST', '/api/me/timeclock/correction-requests', {
        request_type: reqType,
        shift_date: shiftDate,
        punch_id: punchId,
        proposed_clock_in_at:  inTime  ? localToIso(shiftDate, inTime)  : null,
        proposed_clock_out_at: outTime ? localToIso(shiftDate, outTime) : null,
        reason: reason.trim(),
        location_id: defaults?.location_id || null,
      }, headers);
      onSubmitted && onSubmitted();
      onClose();
    } catch (e) {
      let m = e.message; try { m = JSON.parse(e.message).error; } catch {}
      setErr(m);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col fade-in shadow-2xl"
           style={{ maxHeight: '85vh' }}
           onClick={e => e.stopPropagation()}>
        <div className="bg-ink rounded-t-2xl p-5 flex items-center justify-between flex-shrink-0">
          <h3 className="text-cream font-bold text-base">Timeclock correction</h3>
          <button onClick={onClose} className="text-cream/50 hover:text-cream text-3xl leading-none">×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="text-xs text-gray-500 uppercase font-semibold">What happened</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              // CJ 2026-06-15: the original "forgot to clock in entirely"
              // copy missed the split-shift case (clocked in, took a
              // break, forgot to clock back in). Reworded so a staffer
              // recognizes the right option for "add another in/out".
              { id: 'add_punch',  label: 'Add a clock in/out (forgot to clock in, or forgot to clock back in after a break)' },
              { id: 'edit_punch', label: "My times are wrong on a punch I already have" },
            ].map(opt => (
              <button key={opt.id} onClick={() => setReqType(opt.id)}
                style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                          borderRadius: 6,
                          border: `1px solid ${reqType === opt.id ? 'var(--forest)' : '#d0d4d9'}`,
                          background: reqType === opt.id ? 'var(--forest)' : '#fff',
                          color: reqType === opt.id ? '#fff' : '#37474f', cursor: 'pointer' }}>
                {opt.label}
              </button>
            ))}
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Date</div>
            <input type="date" value={shiftDate} onChange={e => setShiftDate(e.target.value)}
              style={{ padding: '8px 10px', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Clock in</div>
              <input type="time" value={inTime} onChange={e => setInTime(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit' }} />
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Clock out</div>
              <input type="time" value={outTime} onChange={e => setOutTime(e.target.value)}
                placeholder="optional"
                style={{ width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit' }} />
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Reason</div>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
              placeholder="What happened? Your manager needs context to approve."
              style={{ width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit', resize: 'none' }} />
          </div>
          {err && <div style={{ background: '#fee', padding: 8, borderRadius: 6, color: '#b91c1c', fontSize: 13 }}>{err}</div>}
        </div>
        <div className="p-4 flex gap-3 flex-shrink-0 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm">Cancel</button>
          <button onClick={submit} disabled={busy}
            className="flex-1 py-3 rounded-xl bg-crimson text-white font-semibold text-sm disabled:opacity-60">
            {busy ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
